import type { ForwardedRef, ReactElement } from 'react';
import { forwardRef } from 'react';

import { useTheme } from '@hivebrite/design-system/Theme';

import DSSection from 'hb-builder/components/BuilderComponents/common/Section/components/DSSection';
import VideoSection from 'hb-builder/components/BuilderComponents/common/Section/components/VideoSection';
import {
  getInnerStyle,
  getWrapperInnerSectionBg,
} from 'hb-builder/components/BuilderComponents/common/Section/style';
import { useRendererContext } from 'hb-builder/contexts/RendererContext';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

interface RenderSectionContentProps {
  children: ReactElement;
  attributes?: SectionItemAttributes;
  isGridSection: boolean;
  isMobile: boolean;
}
export const RenderSectionContent = forwardRef(
  (
    {
      attributes,
      children,
      isGridSection,
      isMobile,
    }: RenderSectionContentProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const theme = useTheme();
    const { bodyWidth, workspaceConfig } = useRendererContext();

    if (!isGridSection || !attributes) {
      return attributes?.backgroundType === 'video' ? (
        <VideoSection
          attributes={attributes}
          children={children}
          isMobile={isMobile}
          ref={ref}
        />
      ) : (
        children
      );
    }

    return (
      <DSSection
        attributes={attributes}
        bodyWidth={bodyWidth}
        gridStyle={getInnerStyle({
          attributes,
          colors: theme?.colors,
          componentType: 'section',
          isMobile,
          isSmallScreen: workspaceConfig.isSmallScreen,
        })}
        ref={ref}
        wrapperStyle={getWrapperInnerSectionBg({
          attributes,
          colors: theme?.colors,
        })}
      >
        {!attributes?.fullWidth &&
          attributes?.containerBackgroundImage &&
          attributes?.containerBackgroundImageAlt && (
            <span
              role="img"
              aria-label={attributes?.containerBackgroundImageAlt}
            />
          )}
        {children}
      </DSSection>
    );
  },
);
